/**
 * Chuẩn header / chân popup EMS (04/11/2026, giống thẻ /settings): header nền trắng (card) sát mép hộp, viền dưới,
 * icon màu chủ đạo tuỳ chọn, tiêu đề đậm; thân nền sáng (background); chân nền trắng, viền trên, nút căn phải.
 * Dùng bởi components/ui/dialog.tsx và alert-dialog.tsx; popup tự dựng header bằng div dùng DIALOG_HEADER_CLASS.
 * Bộ chặn: scripts/scan-dialog-header.cjs (không tự tô màu / nền trên header và tiêu đề).
 */

/** Lề âm kéo header / chân sát mép hộp theo padding thật của hộp (biến --dlg-pad do DialogContent đặt) */
export const DIALOG_HEADER_CLASS =
  "bg-card border-b border-border px-6 py-4 rounded-t-lg mx-[calc(var(--dlg-pad,1.5rem)*-1)] mt-[calc(var(--dlg-pad,1.5rem)*-1)]";

/** Chân: chỉ viền trên + khoảng cách (không kéo sát mép — nhiều chân nằm trong form / vùng cuộn, lề âm sẽ lệch) */
// Hộp xác nhận không có thân (chân ngay sau header): bỏ viền trên để không thành 2 đường kẻ liền nhau
export const DIALOG_FOOTER_CLASS =
  "border-t border-border pt-4 [[data-slot=dialog-header]+&]:border-t-0 [[data-slot=dialog-header]+&]:pt-0 [[data-slot=alert-dialog-header]+&]:border-t-0 [[data-slot=alert-dialog-header]+&]:pt-0";

export const DIALOG_TITLE_CLASS = "text-base leading-snug font-semibold text-foreground";

/** Ô icon trong header: nền nhạt + icon màu chủ đạo (đổi theo màu người dùng chọn ở /settings) */
export const DIALOG_ICON_CLASS =
  "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary [&_svg]:h-[18px] [&_svg]:w-[18px]";

/** Class vị trí giữ ở khung header ngoài; còn lại (bố cục hàng / cột của nội dung) chuyển vào khối nội dung bên trong */
export const HEADER_OUTER_TOKEN = /^(?:[a-z0-9-]+:)*(?:sticky|relative|absolute|top-|z-|shrink-0|sr-only|hidden|block|static)/;

/**
 * Biến --dlg-pad theo padding gốc của hộp. Chuỗi class viết sẵn để Tailwind sinh được (không ghép động).
 * Hỗ trợ p-0…p-8 ở cỡ gốc, sm:, md:.
 */
const PAD_CLASS: Record<string, Record<string, string>> = {
  "": { "0": "[--dlg-pad:0px]", "1": "[--dlg-pad:0.25rem]", "2": "[--dlg-pad:0.5rem]", "3": "[--dlg-pad:0.75rem]", "4": "[--dlg-pad:1rem]", "5": "[--dlg-pad:1.25rem]", "6": "[--dlg-pad:1.5rem]", "8": "[--dlg-pad:2rem]" },
  "sm:": { "0": "sm:[--dlg-pad:0px]", "1": "sm:[--dlg-pad:0.25rem]", "2": "sm:[--dlg-pad:0.5rem]", "3": "sm:[--dlg-pad:0.75rem]", "4": "sm:[--dlg-pad:1rem]", "5": "sm:[--dlg-pad:1.25rem]", "6": "sm:[--dlg-pad:1.5rem]", "8": "sm:[--dlg-pad:2rem]" },
  "md:": { "0": "md:[--dlg-pad:0px]", "1": "md:[--dlg-pad:0.25rem]", "2": "md:[--dlg-pad:0.5rem]", "3": "md:[--dlg-pad:0.75rem]", "4": "md:[--dlg-pad:1rem]", "5": "md:[--dlg-pad:1.25rem]", "6": "md:[--dlg-pad:1.5rem]", "8": "md:[--dlg-pad:2rem]" },
};

export function dialogPadClass(className?: string): string {
  if (typeof className !== "string") return "";
  const out: string[] = [];
  for (const token of className.split(/\s+/)) {
    const m = /^(sm:|md:)?p-(\d+)$/.exec(token);
    if (!m) continue;
    const cls = PAD_CLASS[m[1] || ""]?.[m[2]];
    if (cls) out.push(cls);
  }
  return out.join(" ");
}

/**
 * Class "nhìn" mà header / tiêu đề không được tự đặt (chuẩn nằm ở đây): nền, gradient, viền, bo góc, bóng, padding,
 * màu chữ / cỡ chữ / độ đậm. Lọc ngay lúc render để popup cũ chưa dọn vẫn ra đúng chuẩn (không còn chữ trắng trên nền trắng).
 */
export const HEADER_VISUAL_TOKEN =
  /^(?:[a-z0-9-]+:)*(?:bg-|from-|via-|to-|border|rounded|shadow|p[xytblrse]?-|space-y-|text-(?!left$|center$|right$|start$|end$)|font-(?!mono$))/;
export const TITLE_VISUAL_TOKEN =
  /^(?:[a-z0-9-]+:)*(?:bg-|from-|via-|to-|text-(?!left$|center$|right$|start$|end$)|font-(?!mono$)|leading-|tracking-|uppercase$)/;

/** Tách className của header: phần vị trí → khung ngoài, phần bố cục → khối nội dung; bỏ class nhìn */
export function splitHeaderClass(className?: string): { outer: string; inner: string } {
  if (typeof className !== "string" || !className.trim()) return { outer: "", inner: "" };
  const outer: string[] = [];
  const inner: string[] = [];
  for (const token of className.split(/\s+/).filter(Boolean)) {
    if (HEADER_OUTER_TOKEN.test(token)) outer.push(token);
    else if (!HEADER_VISUAL_TOKEN.test(token)) inner.push(token);
  }
  return { outer: outer.join(" "), inner: inner.join(" ") };
}

/** Bỏ class nhìn khỏi tiêu đề (giữ bố cục: pr-8, truncate, flex, gap…) */
export function titleLayoutClass(className?: string): string {
  if (typeof className !== "string") return "";
  if (/(?:^|\s)sr-only(?:\s|$)/.test(className)) return className;
  return className.split(/\s+/).filter((t) => t && !TITLE_VISUAL_TOKEN.test(t)).join(" ");
}
